import type { Photo, PhotoStatus } from '../types/contract';

export type PhotoSummaryBucket = 'all' | 'mapped' | 'missingLocation' | 'attention';

export const photoStatusLabels: Record<PhotoStatus, string> = {
  ready: '위치 확인됨',
  processing: '등록 중',
  missing_gps: '위치 없음',
  failed: '처리 실패',
};

export const photoSummaryLabels: Record<PhotoSummaryBucket, string> = {
  all: '전체 사진',
  mapped: '위치 확인',
  missingLocation: '위치 없음',
  attention: '처리 실패',
};

export function hasUsableLocation(photo: Photo): boolean {
  return (
    photo.status === 'ready'
    && photo.hasGps
    && photo.latitude != null
    && photo.longitude != null
  );
}

export function getPhotoStatusLabel(status: PhotoStatus | string): string {
  return photoStatusLabels[status as PhotoStatus] ?? status.replace('_', ' ');
}

export function getPhotoStatusHelp(photo: Photo): string {
  if (hasUsableLocation(photo)) {
    return '지도에서 위치를 확인할 수 있는 사진입니다.';
  }

  if (photo.status === 'missing_gps') {
    return '사진은 등록됐지만 브라우저가 받은 파일 안에서 지도 위치를 찾지 못했습니다.';
  }

  if (photo.status === 'failed') {
    return '사진 등록 또는 위치 판정을 완료하지 못했습니다.';
  }

  return '사진을 등록하고 위치 정보를 확인하는 중입니다.';
}

export function getPhotoSummaryCounts(photos: Photo[]) {
  return photos.reduce(
    (counts, photo) => {
      counts.all += 1;

      if (hasUsableLocation(photo)) {
        counts.mapped += 1;
      } else if (photo.status === 'missing_gps') {
        counts.missingLocation += 1;
      } else if (photo.status === 'failed') {
        counts.attention += 1;
      }

      return counts;
    },
    {
      all: 0,
      mapped: 0,
      missingLocation: 0,
      attention: 0,
    },
  );
}

export function filterPhotosBySummaryBucket(photos: Photo[], bucket: PhotoSummaryBucket): Photo[] {
  if (bucket === 'all') {
    return photos;
  }

  if (bucket === 'mapped') {
    return photos.filter(hasUsableLocation);
  }

  if (bucket === 'missingLocation') {
    return photos.filter((photo) => photo.status === 'missing_gps');
  }

  return photos.filter((photo) => photo.status === 'failed');
}
